import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchToken, fetchSaleStatus, fetchAuctionInfo, tokenImage } from "../lib/api";
import { localized } from "../lib/i18nHelpers";
import { ArrowLeft, ExternalLink } from "lucide-react";
import MintModal from "../components/MintModal";
import BidModal from "../components/BidModal";

export default function TokenDetailPage() {
    const { t, i18n } = useTranslation();
    const { number } = useParams();
    const [token, setToken] = useState(null);
    const [status, setStatus] = useState(null);
    const [auction, setAuction] = useState(null);
    const [mintOpen, setMintOpen] = useState(false);
    const [bidOpen, setBidOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const [tk, st] = await Promise.all([fetchToken(+number), fetchSaleStatus()]);
            setToken(tk);
            setStatus(st);
            if (tk.phase === "top_token") {
                setAuction(await fetchAuctionInfo(tk.number));
            }
        } catch (err) {
            console.error("Token detail load failed:", err);
        }
    }, [number]);
    useEffect(() => { load(); }, [load]);

    if (!token) return <div className="p-12 text-slate-500">{t("phase.loading")}</div>;

    const img = (auction?.custom_image_url) || tokenImage(token.lat, token.lon);
    const L = localized(token, i18n);

    return (
        <div data-testid="token-detail-page" className="max-w-7xl mx-auto px-4 md:px-8 py-12">
            <Link
                to="/explorer"
                data-testid="back-explorer"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-frost"
            >
                <ArrowLeft className="w-3 h-3" /> {t("detail.back")}
            </Link>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8">
                <div className="md:col-span-7">
                    <div className="aspect-square md:aspect-[4/3] bg-slate-950 border border-white/10 relative overflow-hidden">
                        {img ? (
                            <img src={img} alt={`#${token.number}`} className="w-full h-full object-cover" />
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="font-serif text-9xl text-frost/30">#{token.number}</div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="md:col-span-5">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                        {t("detail.parcel")}
                    </div>
                    <h1 className="font-serif text-6xl mt-2">#{token.number}</h1>
                    {token.title && (
                        <div className="font-serif text-3xl italic text-gold-bright mt-1">"{L.title}"</div>
                    )}
                    {token.rarity && (
                        <div className="mt-3 inline-block px-3 py-1 text-[10px] uppercase tracking-widest border border-gold text-gold">
                            {t(`auctions.rarity.${token.rarity}`)}
                        </div>
                    )}
                    <div className="mt-4 inline-block ml-2 px-3 py-1 text-[10px] uppercase tracking-widest border border-white/15">
                        {token.phase.replace("_", " ")}
                    </div>
                    {L.quote && (
                        <p className="mt-6 font-serif text-xl italic text-slate-200 border-l-2 border-frost/40 pl-4">
                            "{L.quote}"
                        </p>
                    )}
                    {L.description && (
                        <p className="mt-4 text-sm font-mono text-slate-300 leading-relaxed">
                            {L.description}
                        </p>
                    )}

                    <div className="mt-8 space-y-4 border-t border-b border-white/10 py-6">
                        <Row label={t("detail.latitude")} value={`${token.lat.toFixed(4)}°${token.lat < 0 ? "S" : "N"}`} />
                        <Row label={t("detail.longitude")} value={`${Math.abs(token.lon).toFixed(4)}°${token.lon < 0 ? "W" : "E"}`} />
                        <Row label={token.phase === "top_token" ? t("detail.base_price") : t("detail.price")}
                            value={`${token.price.toLocaleString()} USDC`} accent />
                        <Row label={t("detail.status")} value={token.status.toUpperCase()} />
                        {token.owner && (
                            <Row label={t("detail.owner")}
                                value={`${token.owner.slice(0, 6)}…${token.owner.slice(-4)}`} mono />
                        )}
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                        {token.status === "available" && token.phase !== "top_token" && (
                            <button
                                data-testid="detail-mint"
                                onClick={() => setMintOpen(true)}
                                className="btn-sharp bg-white text-obsidian px-6 py-3 text-xs hover:bg-slate-200"
                            >
                                {t("detail.mint_cta", { price: token.price.toLocaleString() })}
                            </button>
                        )}
                        {token.phase === "top_token" && (
                            <button
                                data-testid="detail-bid"
                                onClick={() => setBidOpen(true)}
                                className="btn-sharp bg-gold text-obsidian px-6 py-3 text-xs hover:opacity-90"
                            >
                                {t("detail.place_bid")}
                            </button>
                        )}
                        <a href="https://opensea.io" target="_blank" rel="noreferrer"
                            data-testid="detail-opensea"
                            className="btn-sharp inline-flex items-center gap-2 border border-white/20 px-6 py-3 text-xs hover:border-frost hover:text-frost">
                            {t("detail.view_opensea")} <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                </div>
            </div>

            <MintModal open={mintOpen} onClose={() => setMintOpen(false)} token={token} status={status} onSuccess={load} />
            <BidModal open={bidOpen} onClose={() => setBidOpen(false)} token={token} auction={auction} onSuccess={load} />
        </div>
    );
}

function Row({ label, value, accent, mono }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</span>
            <span className={`${mono ? "font-mono" : "font-mono"} ${accent ? "text-frost text-base" : "text-white"}`}>
                {value}
            </span>
        </div>
    );
}
